import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import axios from 'axios';
import { CONFIG } from '@/global-config';
import { redirect } from 'next/navigation';

import { fallbackLng } from '@/core/locales';
import { detectLanguage } from '@/core/locales/server';

import { paths } from '@/routes/paths';

const apiInterceptor = () => (error: any) => {
  const { status } = error?.response || {};

  const isClientSide = typeof window !== 'undefined';

  if (status >= 500 || error.code === 'ECONNABORTED') {
    if (isClientSide) {
      window.location.href = paths.error.page500;
    } else {
      redirect(paths.error.page500);
    }

    return null;
  }

  if ([404].includes(status)) {
    if (isClientSide) {
      window.location.href = paths.error.page404;
    } else {
      redirect(paths.error.page404);
    }
    return null;
  }

  if ([403].includes(status)) {
    if (isClientSide) {
      window.location.href = paths.error.page403;
    } else {
      redirect(paths.error.page403);
    }
    return null;
  }

  return Promise.reject(error.response?.data || 'Something went wrong');
};

export class api {
  private static instance: AxiosInstance;

  private static init() {
    if (!CONFIG.serverUrl) throw new Error('Please check .env config');

    api.instance = axios.create({
      baseURL: CONFIG.serverUrl,
    });

    api.instance.interceptors.request.use(
      async (config) =>
        // const lang = await detectLanguage();
        // config.headers['x-data-lang'] = lang || fallbackLng;

        config,
      (error) => Promise.reject(error)
    );

    const handleResponseError = apiInterceptor();
    api.instance.interceptors.response.use((response) => response, handleResponseError);
  }

  private static ensureInitialized() {
    if (!api.instance) {
      api.init();
    }
  }

  private static parseArgs(args: string | [string, AxiosRequestConfig]): {
    url: string;
    config?: AxiosRequestConfig;
  } {
    const [url, config] = Array.isArray(args) ? args : [args];
    return {
      url,
      config: { ...config },
    };
  }

  static async get<T = any>(args: string | [string, AxiosRequestConfig]): Promise<T> {
    api.ensureInitialized();

    const { url, config } = api.parseArgs(args);
    const res = await api.instance.get(url, { ...config });
    return res.data;
  }

  static async post<T>(args: string | [string, AxiosRequestConfig], data?: any): Promise<T> {
    api.ensureInitialized();

    const { url, config } = api.parseArgs(args);
    const response = await api.instance.post<T>(url, data, config);
    return response.data;
  }
}

export class localApi {
  private static instance: AxiosInstance;

  private static init() {
    if (!CONFIG.localApiUrl) throw new Error('Please check .env config');

    localApi.instance = axios.create({
      baseURL: CONFIG.localApiUrl,
    });

    localApi.instance.interceptors.request.use(
      async (config) => {
        const lang = await detectLanguage();
        config.headers['x-data-lang'] = lang || fallbackLng;

        return config;
      },
      (error) => Promise.reject(error)
    );

    const handleResponseError = apiInterceptor();
    localApi.instance.interceptors.response.use((response) => response, handleResponseError);
  }

  private static ensureInitialized() {
    if (!localApi.instance) localApi.init();
  }

  private static parseArgs(args: string | [string, AxiosRequestConfig]) {
    const [url, config] = Array.isArray(args) ? args : [args];
    return { url, config: { ...config } };
  }

  static async post<T>(args: string | [string, AxiosRequestConfig], data?: any): Promise<T> {
    localApi.ensureInitialized();
    const { url, config } = localApi.parseArgs(args);
    const res = await localApi.instance.post(url, data, config);
    return res.data;
  }
}

export const endpoints = {
  newsletter: '/newsletter',
  contact: '/contact-messages',
  bundles: '/public/bundles',
};

export const localEndpoints = {
  pages: {
    getBySlug(slug: string) {
      // Important: no leading slash so it concatenates with baseURL '/api' => '/api/category/:slug'
      return `pages/${encodeURIComponent(slug)}`;
    },
    getPageMetadataBySlug(slug: string) {
      // GET /api/pages/:slug/metadata
      return `pages/${encodeURIComponent(slug)}/metadata`;
    },
  },
  categories: '/categories',
};
